<template>
  <b-modal
    ref="modal"
    size="xl"
    :title="currentVideo ? (currentVideo.name || 'Video') : 'Video'"
    hide-footer
    @hidden="onHidden">

    <div
      class="video-player-container"
      @touchstart="onTouchStart"
      @touchend="onTouchEnd">

      <!--
        :key forces video element to re-mount when switching videos — resets src/playback state.
        playsinline: prevents iOS Safari from forcing full-screen OS media player.
        No autoplay: iOS Safari 15+ blocks autoplay with audio. The user tapped the thumbnail
        to open this modal — one tap on the video to begin playback is standard on mobile.
      -->
      <video
        v-if="currentVideo"
        ref="videoPlayer"
        class="w-100"
        controls
        playsinline
        :key="currentIndex">
        <source
          :src="currentVideo.url"
          :type="currentVideo.contentType || 'video/mp4'" />
        Your browser does not support the video tag.
      </video>
    </div>

    <!-- Navigation bar — only when multiple videos in playlist -->
    <div v-if="videos.length > 1"
         class="d-flex align-items-center justify-content-between mt-2 px-1">
      <b-button
        variant="outline-secondary"
        size="sm"
        :disabled="!hasPrev"
        @click="prev">
        <i class="fas fa-chevron-left mr-1"></i> Previous
      </b-button>
      <small class="text-muted">{{ currentIndex + 1 }} / {{ videos.length }}</small>
      <b-button
        variant="outline-secondary"
        size="sm"
        :disabled="!hasNext"
        @click="next">
        Next <i class="fas fa-chevron-right ml-1"></i>
      </b-button>
    </div>
  </b-modal>
</template>

<script>
/**
 * VideoPlayerModal.vue
 *
 * Bootstrap Vue modal with an HTML5 video player and playlist prev/next navigation.
 * Supports keyboard ← → arrow keys and touch swipe (> 50px threshold) for navigation.
 *
 * Props:
 *   visible    {Boolean} — controls modal visibility (.sync / update:visible)
 *   videos     {Array}   — array of { url, contentType, name, thumbnailUrl }
 *   startIndex {Number}  — index of the video to open first
 *
 * Emits: 'update:visible' for .sync binding
 */
export default {
  name: 'VideoPlayerModal',

  props: {
    visible: { type: Boolean, default: false },
    /** Array of { url, contentType, name, thumbnailUrl } */
    videos: { type: Array, default: () => [] },
    startIndex: { type: Number, default: 0 }
  },

  data () {
    return {
      currentIndex: this.startIndex || 0,
      touchStartX: 0,
      touchEndX: 0
    }
  },

  computed: {
    currentVideo () {
      return this.videos[this.currentIndex] || null
    },
    hasPrev () {
      return this.currentIndex > 0
    },
    hasNext () {
      return this.currentIndex < this.videos.length - 1
    }
  },

  watch: {
    startIndex (val) {
      this.currentIndex = val
    },
    visible (val) {
      if (val) {
        this.currentIndex = this.startIndex
        this.$nextTick(() => this.$refs.modal && this.$refs.modal.show())
      } else {
        this.$refs.modal && this.$refs.modal.hide()
      }
    }
  },

  mounted () {
    window.addEventListener('keydown', this.onKeyDown)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeyDown)
  },

  methods: {
    prev () {
      if (this.hasPrev) {
        this.stopVideo()
        this.currentIndex--
      }
    },

    next () {
      if (this.hasNext) {
        this.stopVideo()
        this.currentIndex++
      }
    },

    stopVideo () {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
        this.$refs.videoPlayer.currentTime = 0
      }
    },

    onHidden () {
      this.stopVideo()
      this.$emit('update:visible', false)
    },

    onKeyDown (e) {
      if (!this.visible) return
      if (e.key === 'ArrowLeft') this.prev()
      if (e.key === 'ArrowRight') this.next()
    },

    onTouchStart (e) {
      this.touchStartX = e.changedTouches[0].screenX
    },

    onTouchEnd (e) {
      this.touchEndX = e.changedTouches[0].screenX
      const delta = this.touchStartX - this.touchEndX
      if (Math.abs(delta) > 50) {
        // Swipe left → next, swipe right → prev
        if (delta > 0) this.next()
        else this.prev()
      }
    }
  }
}
</script>

<style scoped>
.video-player-container {
  width: 100%;
}
</style>
